import bowser from 'bowser'
import { takeEvery, throttle, put, delay, select, spawn, race, take } from 'redux-saga/effects'
import { RUN_AUTOPLAY, AUTOPLAY_DONE } from 'macguffin/components/autoplay/constants'
import { setSpins } from 'macguffin/components/autoplay/actions'
import { ON_INIT, PLAY, PLAY_REQUEST, ERROR, DISPLAY_FREEROUND } from 'macguffin/redux/constants'
import { setPaused, openSettingsScreen } from 'macguffin/redux/actions'
import { SET_DIMENSIONS, ON_RESIZE, POPUP_SCREENS, SET_POPUP_SCREEN } from './constants'
import { setAutoPlayReStart, setPopupScreen, setSpinRingAnimation } from './actions'

export default function* Sagas() {
  yield takeEvery(ON_INIT, onResize, true)
  yield takeEvery(PLAY_REQUEST, onPlayRequest)
  yield takeEvery(PLAY, onPlay)
  yield takeEvery(SET_POPUP_SCREEN, onSetPopupScreen)
  yield takeEvery(AUTOPLAY_DONE, onAutoPlayDone)
  yield takeEvery(ERROR, resetAutoPlayReStart)
  yield takeEvery(action => action.type === DISPLAY_FREEROUND && !action.display, resetAutoPlayReStart)
  yield takeEvery(RUN_AUTOPLAY, onRunAutoplay)

  yield spawn(watchAutoplayCloses)
  yield throttle(200, ON_RESIZE, onResize, false)
}

let lastSizes = {}

function* onResize(initial) {
  const isReallySafari = navigator && /safari/i.test(navigator.userAgent)

  if (!initial && bowser.ios && (!bowser.safari || (bowser.safari && !isReallySafari))) {
    yield delay(50)
  }
  window.scrollTo(0, 0)

  const width = window.innerWidth
  const height = window.innerHeight

  if (lastSizes.width === width && lastSizes.height === height) {
    return
  }
  lastSizes = { width, height }

  const portrait = height >= width
  const desktopUI = width > 900
  const isDesktop = (portrait || desktopUI)

  const gamecontrolScale = Math.min(1, width / 1280, height / 720)

  let spinAreaScale = Math.min(width, height) / 5 / 100
  spinAreaScale = isDesktop ? spinAreaScale : Math.min(1, spinAreaScale * 900 / width)

  let centerYOffset = (desktopUI ? 92 : (height * 0.15))
  if (desktopUI && height < 720) {
    centerYOffset = height * 0.13
  } else if (!desktopUI) {
    if (portrait) {
      centerYOffset = Math.min(0.22 * height, 0.15 * height + 0.115 * width)
    } else {
      centerYOffset = 0.09 * Math.max(document.documentElement.clientHeight || 0, height || 0)
    }
  }
  const availableHeight = height - centerYOffset
  const centerHeight = height - (centerYOffset * 2)
  const centerXOffset = isDesktop ? 0 : width * (0.085 + 0.065 * spinAreaScale)
  const availableWidth = width - centerXOffset
  const centerWidth = width - (centerXOffset * 2)

  const dpr = Math.max(window.devicePixelRatio || 1, 1)

  const dimensions = {
    width,
    height,
    availableWidth,
    availableHeight,
    centerWidth,
    centerHeight,
    centerXOffset,
    centerYOffset,
    portrait,
    dpr,
    desktopUI,
    gamecontrolScale,
    spinAreaScale,
  }

  if (!initial) {
    const oldDesktopUI = yield select(state => state.UI.dimensions.desktopUI)
    if (desktopUI !== oldDesktopUI) {
      yield put(setPopupScreen(null))
    }
  }

  yield put({ type: SET_DIMENSIONS, dimensions })
}

function* onPlayRequest() {
  const { BET, AUTOPLAY, SETTINGS_MENU } = POPUP_SCREENS
  const { autoPlay: { spinsLeft }, UI: { popupScreen } } = yield select()
  if ([BET, AUTOPLAY, SETTINGS_MENU].includes(popupScreen) && !spinsLeft) {
    yield put(setPopupScreen(null))
  }
}

function* onPlay(action) {
  const { params: { enableSpinRingAnimation: enableSpinRingAnimationFromPlay, buyFeature, ga } } = action
  const newEnableSpinRingAnimation = enableSpinRingAnimationFromPlay || (ga === 'spin' && !buyFeature)
  const { enableSpinRingAnimation } = yield select(state => state.UI)
  if (enableSpinRingAnimation !== newEnableSpinRingAnimation) {
    yield put(setSpinRingAnimation(newEnableSpinRingAnimation))
  }
  if (buyFeature) {
    yield put(setSpins(0))
  }
}

function* onSetPopupScreen(action) {
  const { desktopUI } = yield select(state => state.UI.dimensions)
  const { popupScreen } = action
  if (!desktopUI) {
    yield put(setPaused(!!popupScreen))
    if (popupScreen) {
      yield put(openSettingsScreen(null))
    }
  }
}

/**
 * Takes autoplay start and closes popup and settings
 */
function* onRunAutoplay() {
  const {
    UI: { popupScreen },
    base: { settingScreen },
  } = yield select()
  if (popupScreen) {
    yield put(setPopupScreen(null))
  }
  if (settingScreen) {
    yield put(openSettingsScreen(null))
  }
}

/**
 * Opens Autoplay popup when autoplay finishes
 */
function* onAutoPlayDone() {
  const { AUTOPLAY } = POPUP_SCREENS
  const { autoPlayReStart } = yield select(state => state.UI)
  if (autoPlayReStart) {
    yield put(setPopupScreen(AUTOPLAY))
  }
}

/**
 * Watches when autoplay popup closes in playing,
 * without initiating autoplay start and sets spinsLeft to 0
 */
function* watchAutoplayCloses() {
  const { AUTOPLAY } = POPUP_SCREENS
  while (true) {
    const { popupScreen: oldPopupScreen } = yield select(state => state.UI)
    yield take(SET_POPUP_SCREEN)
    const {
      base: { playing },
      autoPlay: { autoPlaying },
      UI: { popupScreen }
    } = yield select()
    if (oldPopupScreen === AUTOPLAY && oldPopupScreen !== popupScreen && playing && !autoPlaying) {
      yield put(setSpins(0))
    }
  }
}

/**
 * Resets autoplay re-start in case of error, finished freerounds
 */
function* resetAutoPlayReStart() {
  const { autoPlayReStart } = yield select(state => state.UI)
  if (autoPlayReStart) {
    yield put(setAutoPlayReStart(false))
  }
}